import clsx from "clsx";
import { useId, type InputHTMLAttributes } from "react";
import { Status } from "./Status";

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: string;
  hint?: string;
  /** Visible error text; sets aria-invalid and the 2 px boundary. */
  error?: string;
  /** Override the 420 px default width. */
  wide?: boolean;
}

/**
 * Text field per 10_component-anatomy.md: visible label always, 40 px control,
 * --border-strong boundary, hint below, and an error that is text plus ARIA plus a
 * thicker boundary — colour is added, never relied on.
 */
export function Field({ label, hint, error, wide = false, className, ...input }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={clsx("flex flex-col gap-2", wide ? "w-full" : "w-full max-w-[420px]", className)}>
      <label htmlFor={id} className="text-small font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(" ") || undefined}
        className={clsx(
          "min-h-10 rounded-sm bg-card px-3 py-2 text-body text-ink placeholder:text-ink-secondary",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
          "disabled:bg-disabled disabled:text-on-disabled",
          error ? "border-2 border-rule-strong" : "border border-rule-strong",
        )}
        {...input}
      />
      {hint && (
        <p id={hintId} className="text-small text-ink-secondary">
          {hint}
        </p>
      )}
      {error && <Status id={errorId} kind="error" label={error} />}
    </div>
  );
}
